import { DialogPanel, DialogTitle } from '@headlessui/react'
import { ClockIcon } from '@heroicons/react/24/outline'
import { format, subDays } from 'date-fns'
import { useState } from 'react'
import { SyncLoader } from 'react-spinners'
import SimpleModal from '../SimpleModal'
import ApifyResourcesHelp from './ApifyResourcesHelp'
import { BACKFILL_TIERS, DEFAULT_BACKFILL_TIER, hintFor } from './scrape-profiles'

const QUICK_RANGES = [7, 14, 30, 90] as const

type Props = {
    /** The monitoring work to historic fetch. `null` closes the modal. */
    monitorTag: string | null
    onClose: () => void
    onConfirm: (since: string, scrapeProfile: string) => Promise<void>
    loading: boolean
}

const isoDate = (date: Date) => format(date, 'yyyy-MM-dd')

/**
 * Ask for a historic scrape of one monitored account, from a fixed date.
 *
 * Built on SimpleModal (Headless UI Dialog) rather than a hand-rolled
 * overlay, so focus is trapped and Escape closes — this is the modal admins
 * reach for most, and it takes keyboard input.
 */
function HistoricFetchModal({ monitorTag, onClose, onConfirm, loading }: Props) {
    const [since, setSince] = useState(() => isoDate(subDays(new Date(), 30)))
    // Always sent, never left to the backend's default: omitting it makes the
    // historic fetch inherit the monitor's profile, which is the daily budget.
    const [scrapeProfile, setScrapeProfile] = useState<string>(DEFAULT_BACKFILL_TIER)

    const days = Math.max(
        0,
        Math.round((Date.now() - new Date(since).getTime()) / (1000 * 60 * 60 * 24))
    )

    return (
        <SimpleModal open={monitorTag !== null} onClose={() => (loading ? null : onClose())}>
            <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-arwen-100 sm:mx-0 sm:h-10 sm:w-10">
                        <ClockIcon className="h-6 w-6 text-arwen-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                        <DialogTitle as="h3" className="text-lg leading-6 text-ink-900">
                            Historic fetch
                        </DialogTitle>
                        <p className="mt-1 text-sm text-ink-500">
                            A monitor&apos;s window rolls forward, so it only ever picks up recent
                            posts. This collects everything back to a date you choose, in one run,
                            and then ends.
                        </p>
                        <p className="mt-1 text-xs text-ink-500 font-mono">{monitorTag}</p>

                        <div className="mt-4">
                            <label
                                className="block text-sm text-ink-700"
                                htmlFor="historic fetch-since"
                            >
                                Collect comments from posts since
                            </label>
                            <input
                                id="historic fetch-since"
                                type="date"
                                value={since}
                                max={isoDate(new Date())}
                                onChange={(e) => setSince(e.target.value)}
                                className="mt-1 block w-full border border-ink-300 rounded-md shadow-sm text-sm focus:ring-arwen-500 focus:border-arwen-500"
                            />
                            <div className="mt-2 flex flex-wrap gap-2">
                                {QUICK_RANGES.map((range) => (
                                    <button
                                        key={range}
                                        type="button"
                                        onClick={() => setSince(isoDate(subDays(new Date(), range)))}
                                        className="px-2 py-1 text-xs rounded-md border border-ink-300 text-ink-700 hover:bg-ink-50"
                                    >
                                        Last {range} days
                                    </button>
                                ))}
                            </div>
                            <p className="mt-2 text-xs text-ink-500">
                                {days} {days === 1 ? 'day' : 'days'} of history.
                            </p>
                        </div>

                        <div className="mt-4">
                            <label
                                className="block text-sm text-ink-700"
                                htmlFor="historic fetch-profile"
                            >
                                Apify resources
                            </label>
                            <div className="mt-1">
                                <ApifyResourcesHelp />
                            </div>
                            <select
                                id="historic fetch-profile"
                                value={scrapeProfile}
                                onChange={(e) => setScrapeProfile(e.target.value)}
                                className="mt-2 block w-full border border-ink-300 rounded-md shadow-sm text-sm focus:ring-arwen-500 focus:border-arwen-500"
                            >
                                {BACKFILL_TIERS.map((tier) => (
                                    <option key={tier.wireValue} value={tier.wireValue}>
                                        {tier.label}
                                    </option>
                                ))}
                            </select>
                            <p className="mt-1 text-xs text-ink-500">
                                {hintFor(scrapeProfile)} Everything else — platform, what to
                                monitor, which posts — is taken from the monitor itself.
                            </p>
                        </div>

                        <p className="mt-4 text-xs text-ink-500">
                            This spends scraping budget in one go, and there is no retry: a failed
                            historic fetch is worth looking at rather than repeating. Asking twice
                            for the same start date starts nothing new.
                        </p>
                    </div>
                </div>

                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                        type="button"
                        onClick={() => onConfirm(since, scrapeProfile)}
                        disabled={loading || !since}
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-arwen-600 text-base text-white hover:bg-arwen-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-arwen-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                        {loading && <SyncLoader color="#fff" size="4px" />}
                        {loading ? '' : 'Historic fetch'}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-ink-300 shadow-sm px-4 py-2 bg-white text-base text-ink-700 hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-arwen-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                        Cancel
                    </button>
                </div>
            </DialogPanel>
        </SimpleModal>
    )
}

export default HistoricFetchModal
